import React from 'react';
import { 
  BarChart3, ShoppingCart, Package, Users, TrendingUp, DollarSign,
  ArrowUpRight, ArrowDownRight, Clock, CheckCircle, Truck, AlertCircle, ChevronLeft,
  Plus, Receipt
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const stats = [
  { label: 'Total Revenue', value: '$12,450', change: '+12.5%', up: true, icon: DollarSign },
  { label: 'Total Orders', value: '156', change: '+8.2%', up: true, icon: ShoppingCart },
  { label: 'Products', value: '370', change: '+3', up: true, icon: Package },
  { label: 'Customers', value: '1,245', change: '+24', up: true, icon: Users },
];

const recentOrders = [
  { id: 'ORD-2024-156', customer: 'John Doe', total: 87.45, status: 'delivered', time: '2 hours ago' },
  { id: 'ORD-2024-155', customer: 'Jane Smith', total: 45.92, status: 'in-transit', time: '3 hours ago' },
  { id: 'ORD-2024-154', customer: 'Mike Johnson', total: 156.78, status: 'processing', time: '4 hours ago' },
  { id: 'ORD-2024-153', customer: 'Sarah Wilson', total: 23.50, status: 'pending', time: '5 hours ago' },
  { id: 'ORD-2024-152', customer: 'Chris Brown', total: 112.30, status: 'delivered', time: '6 hours ago' },
];

const topProducts = [
  { name: 'Golden Premium Rice 25kg', sales: 45, revenue: '$1,169.55' },
  { name: 'MILO 900g', sales: 38, revenue: '$873.62' },
  { name: 'Palm Oil 1L', sales: 32, revenue: '$336.00' },
  { name: 'Indomie Noodles', sales: 120, revenue: '$144.00' },
  { name: 'Knorr Seasoning Cubes', sales: 89, revenue: '$178.00' },
];

const statusConfig = {
  'pending': { color: 'bg-gray-100 text-gray-700', icon: Clock },
  'processing': { color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  'in-transit': { color: 'bg-blue-100 text-blue-700', icon: Truck },
  'delivered': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
};

const AdminDashboard = ({ onBack, onNavigate }) => {
  // Quick action handlers
  const handleAddProduct = () => {
    if (onNavigate) {
      onNavigate('admin-products', { action: 'add' });
    }
  };

  const handleNewOrder = () => {
    if (onNavigate) {
      onNavigate('pos-cashier');
    }
  };

  const handleViewCustomers = () => {
    if (onNavigate) {
      onNavigate('user-management');
    }
  };

  const handleViewReports = () => {
    if (onNavigate) {
      onNavigate('admin-reports');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {onBack && (
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="mb-4 flex items-center gap-1 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Shop
        </Button>
      )}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
          <p className="text-gray-500">Welcome back! Here&apos;s what&apos;s happening with your store.</p>
        </div>
        <Badge className="bg-red-500 text-white px-4 py-2">Admin Mode</Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-blue-600" />
                </div>
                <div className={`flex items-center gap-1 text-sm font-medium ${
                  stat.up ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.up ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {stat.change}
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-800">{stat.value}</p>
              <p className="text-gray-500 text-sm">{stat.label}</p>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Recent Orders */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">Recent Orders</h2>
            <button className="text-blue-500 hover:text-blue-600 text-sm font-medium">View All</button>
          </div>
          <div className="space-y-4">
            {recentOrders.map((order) => {
              const status = statusConfig[order.status];
              const StatusIcon = status.icon;
              return (
                <div key={order.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{order.id}</p>
                      <p className="text-sm text-gray-500">{order.customer}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-800">${order.total.toFixed(2)}</p>
                    <Badge className={`${status.color} text-xs`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {order.status}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Top Products */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">Top Selling Products</h2>
            <button className="text-blue-500 hover:text-blue-600 text-sm font-medium">View All</button>
          </div>
          <div className="space-y-4">
            {topProducts.map((product, index) => (
              <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{product.name}</p>
                    <p className="text-sm text-gray-500">{product.sales} sales</p>
                  </div>
                </div>
                <p className="font-semibold text-green-600">{product.revenue}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="p-6 mt-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button 
            onClick={handleAddProduct}
            className="p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors text-center group"
            data-testid="quick-action-add-product"
          >
            <div className="w-12 h-12 rounded-full bg-blue-100 group-hover:bg-blue-200 mx-auto mb-2 flex items-center justify-center transition-colors">
              <Plus className="w-6 h-6 text-blue-600" />
            </div>
            <p className="font-medium text-blue-800">Add Product</p>
            <p className="text-xs text-blue-600 mt-1">Create new product</p>
          </button>
          <button 
            onClick={handleNewOrder}
            className="p-4 rounded-xl bg-green-50 hover:bg-green-100 transition-colors text-center group"
            data-testid="quick-action-new-order"
          >
            <div className="w-12 h-12 rounded-full bg-green-100 group-hover:bg-green-200 mx-auto mb-2 flex items-center justify-center transition-colors">
              <ShoppingCart className="w-6 h-6 text-green-600" />
            </div>
            <p className="font-medium text-green-800">New Order</p>
            <p className="text-xs text-green-600 mt-1">Open POS terminal</p>
          </button>
          <button 
            onClick={handleViewCustomers}
            className="p-4 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors text-center group"
            data-testid="quick-action-view-customers"
          >
            <div className="w-12 h-12 rounded-full bg-purple-100 group-hover:bg-purple-200 mx-auto mb-2 flex items-center justify-center transition-colors">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <p className="font-medium text-purple-800">View Customers</p>
            <p className="text-xs text-purple-600 mt-1">Manage users</p>
          </button>
          <button 
            onClick={handleViewReports}
            className="p-4 rounded-xl bg-orange-50 hover:bg-orange-100 transition-colors text-center group"
            data-testid="quick-action-view-reports"
          >
            <div className="w-12 h-12 rounded-full bg-orange-100 group-hover:bg-orange-200 mx-auto mb-2 flex items-center justify-center transition-colors">
              <BarChart3 className="w-6 h-6 text-orange-600" />
            </div>
            <p className="font-medium text-orange-800">View Reports</p>
            <p className="text-xs text-orange-600 mt-1">Sales analytics</p>
          </button>
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboard;
